import { ErrorState } from '@ccp/ui';
import type { FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/errors.js';

/**
 * What a person sees when a render fails.
 *
 * The presentation is the design system's `ErrorState`; this is the part that is **this
 * application's**: its language, and its knowledge that an `ApiError` carries a correlation id.
 * That seam is deliberate — a composite that imported i18n or the API client would be a design
 * system that knew about this product.
 *
 * This file used to hold the whole thing, and an identical copy lived in the other application,
 * byte for byte, because there was nowhere shared to put it (M4.5-05).
 */
export function AppErrorState({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation();

  return (
    <ErrorState
      className="mx-auto max-w-(--width-container-narrow) p-(--space-xl)"
      title={t('state.errorTitle')}
      description={t('state.errorBody')}
      reference={error instanceof ApiError ? error.correlationId : undefined}
      retryLabel={t('state.retry')}
      onRetry={resetErrorBoundary}
    />
  );
}
